/**
 * Hero Video Banner - device-aware, visibility-gated video loader
 *
 * Why this needs JS at all:
 * Native <source media="..."> support is inconsistent with autoplay +
 * hidden video combinations (notably iOS Safari), so a "correct file per
 * device, nothing else ever requested" guarantee can only be made by
 * detecting the viewport client-side and setting `src` conditionally.
 *
 * Loading order:
 * 1. Poster renders immediately from the server (no JS involved, LCP-safe).
 * 2. IntersectionObserver confirms the section is actually in view.
 * 3. Only then is the correct (desktop/mobile) video source requested.
 * 4. The video fades in over the poster once it can play.
 *
 * Kept intentionally dependency-free and single-purpose.
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 750px)';

  function initHeroVideo(root) {
    var video = root.querySelector('[data-hero-video-el]');
    if (!video) return;

    var desktopSrc = root.getAttribute('data-hero-video-desktop-src');
    var mobileSrc = root.getAttribute('data-hero-video-mobile-src');

    if (!desktopSrc && !mobileSrc) return;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      // The poster stays as-is; the video is never requested.
      return;
    }

    // Extra iOS Safari autoplay safety, in addition to the muted/playsinline
    // markup attributes already set in the section template.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');

    var mql = window.matchMedia(DESKTOP_QUERY);
    var currentSrc = null;

    function resolveSrc() {
      var isDesktop = mql.matches;
      if (isDesktop) {
        return desktopSrc || mobileSrc;
      }
      return mobileSrc || desktopSrc;
    }

    function loadAndPlay() {
      var nextSrc = resolveSrc();
      if (!nextSrc || nextSrc === currentSrc) return;

      currentSrc = nextSrc;
      video.setAttribute('data-hero-video-loaded', 'false');
      video.src = nextSrc;
      video.load();

      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise
          .then(function () {
            root.setAttribute('data-hero-video-playing', 'true');
            // play() resolving is itself definitive proof frames are
            // rendering - don't wait on a load event that may not fire
            // reliably on every mobile browser (see reveal() below).
            reveal();
          })
          .catch(function () {
            // Autoplay was blocked (e.g. data saver mode): the poster
            // stays visible and the banner remains fully usable.
            root.removeAttribute('data-hero-video-playing');
          });
      }
    }

    function reveal() {
      video.setAttribute('data-hero-video-loaded', 'true');
    }

    // 'loadeddata' alone is not consistently reliable across mobile
    // browsers (notably some Samsung Internet/Chrome Android builds) when
    // `src` is assigned programmatically after preload="none". Multiple
    // independent signals are wired to the same reveal() call - whichever
    // fires first wins, and repeat calls are harmless (idempotent).
    video.addEventListener('loadeddata', reveal);
    video.addEventListener('canplay', reveal);
    video.addEventListener('playing', reveal);

    // If the resolved source fails to load or decode (bad URL, unsupported
    // format, network failure), hide the <video> element entirely rather
    // than leaving its default black canvas on screen - the poster (or the
    // scheme-colored fallback background) stays visible instead.
    video.addEventListener('error', function () {
      video.style.display = 'none';
      root.removeAttribute('data-hero-video-playing');
    });

    // Re-evaluate on breakpoint changes (e.g. tablet rotation) so the
    // correct file is always the one actually playing.
    var changeHandler = function () {
      loadAndPlay();
    };

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', changeHandler);
    } else if (typeof mql.addListener === 'function') {
      // Older Safari fallback
      mql.addListener(changeHandler);
    }

    // Visibility-gated load: the hero is typically already in the initial
    // viewport, so this fires effectively immediately on first paint -
    // but it also protects performance if this section is ever reused
    // further down a page, or inside an app block.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              loadAndPlay();
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px', threshold: 0 }
      );
      observer.observe(root);
    } else {
      // No IntersectionObserver support: fall back to immediate load.
      loadAndPlay();
    }
  }

  function init() {
    var sections = document.querySelectorAll('[data-hero-video]');
    sections.forEach(initHeroVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
